import {
  observable, action, makeObservable, computed,
} from 'mobx';
import Cookie from 'js-cookie';
import client from 'utils/graphql';
import decodeToken from 'utils/decode-token';
import toLoggedInUser from 'utils/to-logged-in-user';
import hasAnyRole from 'utils/has-any-role';
import { PORTAL_ROLES } from 'utils/constants';

import login from 'graphql/mutations/login.gql';
import lostPassword from 'graphql/mutations/lostPassword.gql';
import resetPassword from 'graphql/mutations/resetPassword.gql';
import me from 'graphql/queries/me.gql';
import MY_ROYALTY_QUERY from 'graphql/queries/my-royalty';

export default class Authentication {
  static storeName = 'Authentication';

  client = null;

  token = null;

  userId = null;

  roles = [];

  loggedInUser = null;

  constructor(context) {
    makeObservable(this, {
      loggedInUser: observable,
      roles: observable,
      hasPortalAccess: computed,
      login: action.bound,
      requestPasswordResetLink: action.bound,
      resetPassword: action.bound,
      logout: action.bound,
      myRoyalty: action.bound,
    });
    Object.assign(this, context);

    this.client = client(this.token);
  }

  get hasPortalAccess() {
    return hasAnyRole(this.roles, PORTAL_ROLES);
  }

  get isLoggedIn() {
    const token = this.token || Cookie.get('auth_token');

    if (!token) return false;

    this.setToken(token);

    if (!this.hasPortalAccess) {
      this.logout();
      return false;
    }

    if (this.loggedInUser) return true;

    return this.me();
  }

  async login(credentials) {
    const query = login.loc.source.body;
    try {
      const result = await this.client.mutation(query, { ...credentials, roles: PORTAL_ROLES });

      this.storeToken(result.loginDashboard.token);

      return null;
    } catch (error) {
      return error.message;
    }
  }

  async requestPasswordResetLink({ email }) {
    const query = lostPassword.loc.source.body;
    try {
      await this.client.mutation(query, { email, site: 'partner' });

      return null;
    } catch (error) {
      return error.message;
    }
  }

  async resetPassword({ email, token, password }) {
    const query = resetPassword.loc.source.body;
    try {
      const result = await this.client.mutation(query, { data: { email, token, password } });

      this.storeToken(result.resetPassword.token);

      return null;
    } catch (error) {
      return error.message;
    }
  }

  storeToken(token) {
    Cookie.set('auth_token', token);
    this.setToken(token);
  }

  setToken(token) {
    this.token = token;
    this.roles = decodeToken(token).roles;
  }

  logout() {
    this.loggedInUser = null;
    this.setToken(null);
    Cookie.remove('auth_token');
  }

  async myRoyalty() {
    try {
      const response = await this.client.query(MY_ROYALTY_QUERY);

      return { royalty: response.me.user.royalty || { rates: [] } };
    } catch (error) {
      return { error: error.message };
    }
  }

  async me() {
    const query = me.loc.source.body;
    try {
      const response = await this.client.query(query);

      this.loggedInUser = toLoggedInUser(response.me.user);

      return true;
    } catch (error) {
      console.error('Authentication check failed', error);
      this.logout();

      return false;
    }
  }
}
